import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { keyframes } from '@emotion/react';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const shake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  20% { transform: rotate(-8deg); }
  40% { transform: rotate(7deg); }
  60% { transform: rotate(-5deg); }
  80% { transform: rotate(4deg); }
`;

const blobDrift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(24px, -18px) scale(1.08); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

// หน้า "ไม่มีสิทธิ์เข้าถึง" — ใช้เป็น route guard ที่ระดับ layout (ดู hasModuleAccess ใน
// utils/functions/accessRight.js) กัน user ที่ login แล้วแต่ไม่มีสิทธิ์โมดูลนั้น พิมพ์ URL เดา
// เข้าถึงหน้าได้ตรงๆ (เดิม sidebar แค่ซ่อนเมนูไม่ให้เห็น ไม่เคยกันที่ระดับหน้าเลย) — โทนสีส้ม/แดง
// (ต่างจาก NotFound.js ที่เป็นโทน primary indigo) ให้แยกความรู้สึกจาก 404 ชัดเจน: "หน้ามีจริง แต่คุณ
// เข้าไม่ได้" ไม่ใช่ "หน้าไม่มีอยู่จริง"
export default function AccessDenied({ moduleLabel }) {
  const history = useHistory();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        px: 2,
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '14%',
          right: '12%',
          width: 240,
          height: 240,
          borderRadius: '50%',
          background: (theme) => `radial-gradient(circle, ${theme.palette.warning.light} 0%, transparent 70%)`,
          opacity: 0.35,
          filter: 'blur(10px)',
          animation: `${blobDrift} 9.5s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '12%',
          left: '10%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: (theme) => `radial-gradient(circle, ${theme.palette.error.light} 0%, transparent 70%)`,
          opacity: 0.3,
          filter: 'blur(10px)',
          animation: `${blobDrift} 11s ease-in-out infinite reverse`,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          textAlign: 'center',
          maxWidth: 460,
          animation: `${fadeUp} 0.5s ease-out`,
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) => `linear-gradient(135deg, ${theme.palette.error.dark || theme.palette.error.main} 0%, ${theme.palette.warning.main} 100%)`,
            boxShadow: (theme) => `0 16px 32px -12px ${theme.palette.error.main}66`,
            animation: `${shake} 2.6s ease-in-out infinite`,
            animationDelay: '0.6s',
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 44, color: '#fff' }} />
        </Box>

        <Typography
          sx={{
            fontSize: { xs: '2.1rem', sm: '2.6rem' },
            fontWeight: 800,
            lineHeight: 1.15,
            backgroundImage: (theme) => `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          ไม่มีสิทธิ์เข้าถึง
        </Typography>

        <Typography variant="h6" fontWeight={700} sx={{ mt: 1.5 }}>
          {moduleLabel ? `คุณไม่มีสิทธิ์ใน "${moduleLabel}"` : 'คุณไม่มีสิทธิ์ใช้งานเมนูนี้'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 3.5 }}>
          หากคิดว่าควรมีสิทธิ์เข้าถึง กรุณาติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์เพิ่มเติม
        </Typography>

        <Button
          variant="contained"
          size="large"
          startIcon={<HomeOutlinedIcon />}
          onClick={() => history.push('/')}
          sx={{
            borderRadius: 2.5,
            px: 4,
            boxShadow: (theme) => `0 10px 24px -8px ${theme.palette.error.main}80`,
          }}
        >
          กลับสู่หน้าหลัก
        </Button>
      </Box>
    </Box>
  );
}

AccessDenied.propTypes = {
  moduleLabel: PropTypes.string,
};

AccessDenied.defaultProps = {
  moduleLabel: null,
};
